// Pure helpers for removing a single answer line from an assessment plugin and
// reflowing the rest. Answers live in indexed sortable containers (sc-Answer0,
// sc-Answer1, …) and getRenderTemplate maps loop index → container + letter, so
// once the content shifts up and nBoxes shrinks the A/B/C re-lettering is automatic.
//
// These functions are pure so the index math (the off-by-one-prone part) is unit
// tested independently of the React/redux wiring in EditorShortcuts.

import { MATCHING_RIGHT_OFFSET } from "../../../../common/constants";

// Box-level text formatting props a text box (BasicText/RichText) stores on its
// toolbar state and the wrapper renders (see EditorBox textFormatStyle). Unlike
// color/font-family — which CKEditor keeps inline in __text — these live ONLY
// here: the B/I/U pills are flags, and font size is stripped out of the HTML
// into textFontSize on commit. Reorder must carry them alongside __text or
// size/italic get left behind while color/font appear to move.
export const TEXT_FORMAT_KEYS = [
  "fontScale",
  "textAlign",
  "textFontFamily",
  "textFontSize",
  "textColor",
  "highlightColor",
  "bold",
  "italic",
  "underline",
];

/**
 * Remove the answer at `deleteIndex`, preserving the order of the rest.
 * @param {string[]} answerTexts verbatim __text (HTML) of each answer, index 0..N-1
 * @param {number} deleteIndex answer index to remove
 * @returns {string[]} length N-1
 */
export function reflowAnswerTexts(answerTexts, deleteIndex) {
  return answerTexts.filter((_, i) => i !== deleteIndex);
}

/**
 * Adjust the stored correctAnswer after removing the answer at `deleteIndex`.
 * Handles both shapes used across the assessment plugins:
 *  - scalar index (MultipleChoice / TrueFalse / single-correct Ordering)
 *  - array of indices (MultipleAnswer selected set, Ordering permutation)
 * Non-indexed values (e.g. a FreeResponse string, undefined) pass through.
 */
export function reflowCorrectAnswer(correctAnswer, deleteIndex) {
  if (Array.isArray(correctAnswer)) {
    // Numeric array = index set (MultipleAnswer) or permutation (Ordering): drop
    // the value and shift larger indices down. Non-numeric array = positional
    // (e.g. TrueFalse "true"/"false" per statement): drop the slot at deleteIndex.
    const allNumeric = correctAnswer.every((v) => typeof v === "number");
    if (allNumeric) {
      return correctAnswer
        .filter((x) => x !== deleteIndex)
        .map((x) => (x > deleteIndex ? x - 1 : x));
    }
    return correctAnswer.filter((_, i) => i !== deleteIndex);
  }
  if (typeof correctAnswer === "number") {
    if (correctAnswer === deleteIndex) return 0; // deleted the correct one → reset to first
    return correctAnswer > deleteIndex ? correctAnswer - 1 : correctAnswer;
  }
  return correctAnswer;
}

// Plugins whose answer list reflows on a per-index delete (scalar/array
// correctAnswer). DragDrop/FillInBlank are excluded (their {{#N}} / #----#
// markers need separate handling); Matching's correctAnswer is pair-shaped.
export const REFLOW_PLUGINS = ["MultipleChoice", "MultipleAnswer", "TrueFalse", "Ordering"];

// Remove answer `k` from assessment box `boxId` and reflow the rest, using the
// injected editor operations (so both EditorShortcuts and EditorApp share one
// tested path). `state` = { boxes, pluginToolbars, exercises } (redux present).
// `ops` = { setText(childId, text), setCorrectAnswer(boxId, ca, page), setNBoxes(boxId, n) }.
// Returns true when it handled the delete (incl. a deliberate no-op blocking the
// last answer), false when it isn't applicable (caller may fall back).
export function applyAnswerRemoval(state, boxId, k, ops) {
  const { boxes, pluginToolbars, exercises } = state;
  const parentBox = boxes && boxes[boxId];
  const parentTb = pluginToolbars && pluginToolbars[boxId];
  if (!parentBox || !parentTb || !parentBox.sortableContainers) return false;
  if (!REFLOW_PLUGINS.includes(parentTb.pluginId)) return false;
  const N = parentTb.state && parentTb.state.nBoxes;
  if (!N || k < 0 || k >= N) return false;
  if (N <= 1) return true; // keep at least one answer — block the delete

  const childIds = [];
  for (let i = 0; i < N; i++) {
    const c = parentBox.sortableContainers["sc-Answer" + i];
    childIds[i] = c && c.children && c.children[0];
  }
  if (childIds.some((id) => id == null)) return false; // unexpected shape

  const currentTexts = childIds.map((id) =>
    pluginToolbars[id] && pluginToolbars[id].state && pluginToolbars[id].state.__text != null
      ? pluginToolbars[id].state.__text
      : ""
  );
  const shifted = reflowAnswerTexts(currentTexts, k); // length N-1
  for (let j = k; j < shifted.length; j++) {
    ops.setText(childIds[j], shifted[j]);
  }

  const page = parentBox.parent;
  const exEntry =
    exercises && exercises[page] && exercises[page].exercises && exercises[page].exercises[boxId];
  if (exEntry) {
    ops.setCorrectAnswer(boxId, reflowCorrectAnswer(exEntry.correctAnswer, k), page);
  }
  ops.setNBoxes(boxId, N - 1); // shrink last → re-letters automatically
  return true;
}

// Renumber DragDrop {{#N}} slots after removing answer index k (0-based; slot
// numbers are 1-based, so the removed slot is k+1). Removes {{#k+1}} (and one
// adjacent leading space) and shifts every higher {{#m}} down by one.
//
// Matching: move the RIGHT-column text of row `from` up/down by swapping it with
// row `to`. Matching scoring is positional (left row i pairs with right row i;
// the right cell of row i lives in sc-Answer{i+MATCHING_RIGHT_OFFSET}, a
// count-independent key), so re-pairing in the editor is exactly a swap of two
// right cells — no nBoxes change, no correctAnswer.
// Replaces editor drag-and-drop for Matching (drag stays Visor-only).
// `ops` = { setText(childId, text) }.
export function applyMatchingRightSwap(state, boxId, from, to, ops) {
  const { boxes, pluginToolbars } = state;
  const box = boxes && boxes[boxId];
  const tb = pluginToolbars && pluginToolbars[boxId];
  if (!box || !tb || tb.pluginId !== "Matching" || !box.sortableContainers) return false;
  const N = tb.state && tb.state.nBoxes;
  if (!N || from < 0 || from >= N || to < 0 || to >= N || from === to) return false;
  const cFrom = box.sortableContainers["sc-Answer" + (from + MATCHING_RIGHT_OFFSET)];
  const cTo = box.sortableContainers["sc-Answer" + (to + MATCHING_RIGHT_OFFSET)];
  const idFrom = cFrom && cFrom.children && cFrom.children[0];
  const idTo = cTo && cTo.children && cTo.children[0];
  if (idFrom == null || idTo == null) return false;
  const txt = (id) =>
    pluginToolbars[id] && pluginToolbars[id].state && pluginToolbars[id].state.__text != null
      ? pluginToolbars[id].state.__text
      : "";
  const tFrom = txt(idFrom);
  const tTo = txt(idTo);
  ops.setText(idFrom, tTo);
  ops.setText(idTo, tFrom);
  return true;
}

// Matching: remove pair `k` — the left prompt lives in sc-Answer{k} and its
// paired right answer in sc-Answer{k+MATCHING_RIGHT_OFFSET}. Reflow BOTH columns
// up (drop index k, shift the rest) and shrink nBoxes. Scoring is positional and
// correctAnswer is not index-based here, so there's no correctAnswer reflow.
// `ops` = { setText(childId, text), setNBoxes(boxId, n) }.
export function applyMatchingAnswerRemoval(state, boxId, k, ops) {
  const { boxes, pluginToolbars } = state;
  const box = boxes && boxes[boxId];
  const tb = pluginToolbars && pluginToolbars[boxId];
  if (!box || !tb || tb.pluginId !== "Matching" || !box.sortableContainers) return false;
  const N = tb.state && tb.state.nBoxes;
  if (!N || k < 0 || k >= N) return false;
  if (N <= 1) return true; // keep at least one pair — block the delete

  const leftIds = [];
  const rightIds = [];
  for (let i = 0; i < N; i++) {
    const cl = box.sortableContainers["sc-Answer" + i];
    const cr = box.sortableContainers["sc-Answer" + (i + MATCHING_RIGHT_OFFSET)];
    leftIds[i] = cl && cl.children && cl.children[0];
    rightIds[i] = cr && cr.children && cr.children[0];
  }
  if (leftIds.some((id) => id == null) || rightIds.some((id) => id == null)) return false;

  const txt = (id) =>
    pluginToolbars[id] && pluginToolbars[id].state && pluginToolbars[id].state.__text != null
      ? pluginToolbars[id].state.__text
      : "";
  const leftShifted = reflowAnswerTexts(leftIds.map(txt), k); // length N-1
  const rightShifted = reflowAnswerTexts(rightIds.map(txt), k);
  for (let j = k; j < leftShifted.length; j++) {
    ops.setText(leftIds[j], leftShifted[j]);
    ops.setText(rightIds[j], rightShifted[j]);
  }
  ops.setNBoxes(boxId, N - 1); // shrink last pair; renders reflow automatically
  return true;
}

// (Adding a Matching pair no longer needs a right-column remap: the right column
// is keyed count-independently at sc-Answer{i+MATCHING_RIGHT_OFFSET}, so growing
// nBoxes simply appends one new left + one new right container without disturbing
// existing pairs. The former planMatchingAdd helper was removed with that fix.)

export function renumberDndSlots(text, k) {
  const removed = k + 1;
  let out = (text || "").replace(new RegExp("\\s?\\{\\{#" + removed + "\\}\\}"), "");
  out = out.replace(/\{\{#(\d+)\}\}/g, (m, n) => {
    const num = parseInt(n, 10);
    return num > removed ? "{{#" + (num - 1) + "}}" : m;
  });
  return out;
}

// Move element `from` -> `to` in an array (AtomicBox grip-drag semantics: pull
// the item out and reinsert at the target slot). Pure — returns a new array.
export function moveInArray(arr, from, to) {
  const out = arr.slice();
  const [m] = out.splice(from, 1);
  out.splice(to, 0, m);
  return out;
}

// order[newPos] = oldIdx — the post-move arrangement of the original indices.
function moveOrder(n, from, to) {
  const seq = [];
  for (let i = 0; i < n; i++) seq.push(i);
  return moveInArray(seq, from, to);
}

// Remap correctAnswer after answer `from` is moved to `to`, so "correctness"
// follows the text to its new row. Handles every shape:
//  - scalar index (MC): the correct index moves to its new position
//  - numeric array (MA set / DragDrop dnd dropzone→answer map): each index value
//    moves to its new position
//  - positional array (TrueFalse true/false per row): the per-row value rides
//    along to the row's new position
export function moveCorrectAnswer(correctAnswer, n, from, to) {
  const order = moveOrder(n, from, to); // order[newPos] = oldIdx
  const oldToNew = new Array(n);
  order.forEach((oldIdx, newPos) => {
    oldToNew[oldIdx] = newPos;
  });
  if (Array.isArray(correctAnswer)) {
    const allNumeric = correctAnswer.every((v) => typeof v === "number");
    if (allNumeric) {
      return correctAnswer.map((v) => (oldToNew[v] != null ? oldToNew[v] : v));
    }
    return order.map((oldIdx) => correctAnswer[oldIdx]); // positional
  }
  if (typeof correctAnswer === "number") {
    return oldToNew[correctAnswer] != null ? oldToNew[correctAnswer] : correctAnswer;
  }
  return correctAnswer;
}

// Reorder answers by moving the answer at `from` to position `to` (AtomicBox
// grip-drag). Rewrites the answer texts into the fixed sc-Answer{i} containers
// in their new order and remaps correctAnswer. nBoxes is unchanged (no container
// create/delete), so this is safe for MC/MA/TrueFalse/Ordering/DragDrop without
// any marker renumber. Matching's dual left/right columns need a dedicated move
// and are excluded. `ops` = { setText(childId, text), setCorrectAnswer(boxId, ca, page) }.
export function applyAnswerMove(state, boxId, from, to, ops) {
  const { boxes, pluginToolbars, exercises } = state;
  const parentBox = boxes && boxes[boxId];
  const parentTb = pluginToolbars && pluginToolbars[boxId];
  if (!parentBox || !parentTb || !parentBox.sortableContainers) return false;
  if (parentTb.pluginId === "Matching") return false; // dual-column move not handled here
  const N = parentTb.state && parentTb.state.nBoxes;
  if (!N || from < 0 || from >= N || to < 0 || to >= N) return false;
  if (from === to) return true; // no-op

  const childIds = [];
  for (let i = 0; i < N; i++) {
    const c = parentBox.sortableContainers["sc-Answer" + i];
    childIds[i] = c && c.children && c.children[0];
  }
  if (childIds.some((id) => id == null)) return false;

  const txt = (id) =>
    pluginToolbars[id] && pluginToolbars[id].state && pluginToolbars[id].state.__text != null
      ? pluginToolbars[id].state.__text
      : "";
  // Snapshot each answer's box-level format (only the keys it actually carries),
  // so the reorder moves it in lockstep with the text.
  const fmt = (id) => {
    const st = pluginToolbars[id] && pluginToolbars[id].state;
    const out = {};
    if (st) TEXT_FORMAT_KEYS.forEach((k) => st[k] !== undefined && (out[k] = st[k]));
    return out;
  };
  const sameFmt = (a, b) => {
    const ak = Object.keys(a);
    return ak.length === Object.keys(b).length && ak.every((k) => a[k] === b[k]);
  };
  const oldTexts = childIds.map(txt);
  const newTexts = moveInArray(oldTexts, from, to);
  const oldFmts = childIds.map(fmt);
  const newFmts = moveInArray(oldFmts, from, to);
  for (let i = 0; i < N; i++) {
    if (newTexts[i] === oldTexts[i] && sameFmt(newFmts[i], oldFmts[i])) continue;
    // Prefer the combined op: it sets __text AND the format verbatim (and clears
    // format keys the incoming answer lacks) in one write. Callers without it
    // fall back to text-only reorder (the pre-fix behavior).
    if (ops.setAnswerContent) ops.setAnswerContent(childIds[i], newTexts[i], newFmts[i]);
    else ops.setText(childIds[i], newTexts[i]);
  }

  const page = parentBox.parent;
  const exEntry =
    exercises && exercises[page] && exercises[page].exercises && exercises[page].exercises[boxId];
  if (exEntry) {
    ops.setCorrectAnswer(boxId, moveCorrectAnswer(exEntry.correctAnswer, N, from, to), page);
  }
  return true;
}

// DragDrop answer removal: shift answer texts up, renumber the {{#N}} slots in

// DragDrop answer removal: shift answer texts up, renumber the {{#N}} slots in
// the question, reflow the dnd correctAnswer (array indexed by dropzone), and
// shrink nBoxes. `ops` adds setQuestionText(questionChildId, text) on top of the
// applyAnswerRemoval ops.
export function applyDragDropAnswerRemoval(state, boxId, k, ops) {
  const { boxes, pluginToolbars, exercises } = state;
  const parentBox = boxes && boxes[boxId];
  const parentTb = pluginToolbars && pluginToolbars[boxId];
  if (
    !parentBox ||
    !parentTb ||
    parentTb.pluginId !== "DragDrop" ||
    !parentBox.sortableContainers
  ) {
    return false;
  }
  const N = parentTb.state && parentTb.state.nBoxes;
  if (!N || k < 0 || k >= N) return false;
  if (N <= 1) return true;

  const childIds = [];
  for (let i = 0; i < N; i++) {
    const c = parentBox.sortableContainers["sc-Answer" + i];
    childIds[i] = c && c.children && c.children[0];
  }
  if (childIds.some((id) => id == null)) return false;
  const currentTexts = childIds.map((id) =>
    pluginToolbars[id] && pluginToolbars[id].state && pluginToolbars[id].state.__text != null
      ? pluginToolbars[id].state.__text
      : ""
  );
  const shifted = reflowAnswerTexts(currentTexts, k);
  for (let j = k; j < shifted.length; j++) ops.setText(childIds[j], shifted[j]);

  const sq = parentBox.sortableContainers["sc-Question"];
  const qChild = sq && sq.children && sq.children[0];
  if (qChild && pluginToolbars[qChild] && pluginToolbars[qChild].state) {
    let qText = pluginToolbars[qChild].state.__text || "";
    try {
      if (qText.includes("%")) qText = decodeURIComponent(qText);
    } catch (e) {
      /* keep original */
    }
    ops.setQuestionText(qChild, renumberDndSlots(qText, k));
  }

  const page = parentBox.parent;
  const exEntry =
    exercises && exercises[page] && exercises[page].exercises && exercises[page].exercises[boxId];
  if (exEntry && Array.isArray(exEntry.correctAnswer)) {
    const newCa = exEntry.correctAnswer
      .filter((_, i) => i !== k)
      .map((v) => (typeof v === "number" && v > k ? v - 1 : v));
    ops.setCorrectAnswer(boxId, newCa, page);
  }

  ops.setNBoxes(boxId, N - 1);
  return true;
}
